import { Link, useParams } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import { Activity, Building2, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useViewBreakdown } from '@/hooks/useSuperadmin';
import { DailyChart } from '@/components/superadmin/analytics/charts';
import { Kpi, PageFrame, RangeToggle } from '@/components/superadmin/analytics/shared';
import { nf, useDays } from '@/components/superadmin/analytics/utils';

/** Level 2 (by screen): which organisations use one screen. Aggregates only. */
const SuperadminViewAnalytics = () => {
  const { view = '' } = useParams();
  const [days, setDays, q] = useDays();
  const { data, loading, error } = useViewBreakdown(view, days);
  const max = Math.max(1, ...(data?.tenants ?? []).map((t) => t.uses));

  return (
    <PageFrame title={`Screen: ${view}`} crumbs={[{ label: 'Analytics', to: `/superadmin/analytics${q}` }, { label: view }]} subtitle="Usage of this screen across organisations." right={<RangeToggle days={days} onChange={setDays} />}>
      {error && <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm">Could not load this screen: {error}</p>}
      {loading && !data ? <Skeleton className="h-28 w-full" /> : data ? (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Kpi label="Uses" value={data.totals.uses} hint={`last ${days} days`} icon={Activity} />
            <Kpi label="Users" value={data.totals.users} hint="distinct" icon={Users} />
            <Kpi label="Organisations" value={data.totals.tenants} hint="using this screen" icon={Building2} />
          </div>
          {data.totals.uses === 0 ? <p className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">Nobody used this screen in the last {days} days.</p> : (
            <>
              <Card><CardHeader><CardTitle className="text-base">Daily use</CardTitle><CardDescription>Uses (left axis) and unique users (right axis)</CardDescription></CardHeader><CardContent><DailyChart data={data.daily} height={240} /></CardContent></Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Organisations using it</CardTitle><CardDescription>Select an organisation to drill in</CardDescription></CardHeader>
                <CardContent className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th scope="col" className="py-2 text-start font-normal">Organisation</th><th scope="col" className="py-2 text-start font-normal">Plan</th><th scope="col" className="py-2 text-end font-normal">Users</th>
                      <th scope="col" className="py-2 ps-6 text-start font-normal">Uses</th><th scope="col" className="py-2 text-end font-normal">Last used</th></tr></thead>
                    <tbody>
                      {data.tenants.map((o) => (
                        <tr key={o.tenant_id} className="border-b border-border last:border-0 hover:bg-secondary/50">
                          <td className="py-2.5"><Link to={`/superadmin/analytics/org/${o.tenant_id}${q}`} className="font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded">{o.name}</Link> <span className="font-mono text-xs text-muted-foreground">{o.slug}</span></td>
                          <td className="py-2.5 capitalize">{o.plan}</td>
                          <td className="py-2.5 text-end font-mono">{nf(o.users)}</td>
                          <td className="py-2.5 ps-6"><span className="flex items-center gap-2"><span className="block h-1.5 w-28 overflow-hidden rounded-full bg-muted" aria-hidden="true"><span className="block h-full rounded-full bg-primary" style={{ width: `${(o.uses / max) * 100}%` }} /></span><span className="font-mono text-xs">{nf(o.uses)}</span></span></td>
                          <td className="py-2.5 text-end text-xs text-muted-foreground">{formatDistanceToNowStrict(new Date(o.last_used))} ago</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          )}
        </>
      ) : null}
    </PageFrame>
  );
};

export default SuperadminViewAnalytics;
